import { Injectable, signal } from '@angular/core';
import { UserInterface } from '../user.interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth-token';
  private apiUrl = environment.apiUrl;

  currentUserSig = signal<UserInterface | undefined | null>(undefined);

  constructor(private http: HttpClient) { }

  /**
   * Verifica se o usuário está logado com base na existência do token no sessionStorage.
   */
  isLoggedIn(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired();
  }

  /**
   * Obtém o token JWT
   */
  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Verifica se o token expirou.
   */
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }

    try {
      const payload = this.decodeToken(token);
      if (payload && payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return currentTime > payload.exp;
      }
      return true;
    } catch (error) {
      console.error('Erro ao decodificar o token:', error);
      return true;
    }
  }

  /**
   * Decodifica um token JWT.
   */
  private decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  /**
   * Obtém informações do usuário a partir do token
   */
  getUserFromToken(): any {
    const token = this.getToken();
    if (!token) return null;

    try {
      return this.decodeToken(token);
    } catch (error) {
      console.error('Erro ao obter usuário do token:', error);
      return null;
    }
  }

  /**
   * Faz uma requisição autenticada com o token JWT
   */
  makeAuthenticatedRequest(endpoint: string) {
    const token = this.getToken();
    if (!token) {
      throw new Error('Token não disponível');
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(`${this.apiUrl}/${endpoint}`, { headers });
  }

  /**
   * Limpa dados de autenticação
   */
  clearAuth() {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem('shortName');
    sessionStorage.removeItem('roles');
    this.currentUserSig.set(null);
  }
}